import { pricingPlansModalOpenAtom } from '../../billingAtoms';
import { useSubscriptionUpgrade } from '../../hooks/useSubscriptionUpgrade';
import { pricingPlansModalMessages } from './pricing-plans-modal.messages';

import { ButtonAction } from '@heidi/analytics/vocabulary';
import { Period } from '@heidi/commerce/contracts';
import { useUserBillingInfo } from '@heidi/commerce/data';
import { TierProFilled } from '@heidi/design-system/icons';
import { BillingSeatType, SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useSetAtom } from 'jotai';
import { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useReverseTrial } from '../../../../../data/injected-billing';
import { useClinicianTrialYearlyMaxOffer } from '../../../../trials-and-upgrades/clinician-trial-end/use-clinician-trial-yearly-max-offer';
import { PlanPeriodTabs } from '../../../../trials-and-upgrades/plan-period-tabs/plan-period-tabs';
import type { BillingPeriod } from '../../../../trials-and-upgrades/plan-period-tabs/types';
import { PlanPriceDisplay } from '../../../../trials-and-upgrades/plan-price-display/plan-price-display';
import { isClinicianMaxBillingSeat } from '../../../is-clinician-max-billing-seat';
import { useComplimentaryYearlyMaxOffer } from '../../../use-complimentary-yearly-max-offer';
import { getCommerceBillingUiPorts } from '../../billing-ui-ports';
import { clinicianMaxPricingPlanCardMessages as messages } from './clinician-max-pricing-plan-card.messages';
import {
  PRICING_PLANS_MODAL_CARD_CLASSES,
  PricingPlanCardBase,
} from './pricing-plan-card-base';
import { PricingPlanCardCtaButton } from './pricing-plan-card-cta-button';
import { usePricingPlansModalFeatures } from './pricing-plans-modal.features';
import { trackPricingPlansModalCta } from './track-pricing-plans-modal-cta';
import { usePlanPriceDisplay } from './use-plan-price-display';

/**
 * Clinician Max card in the pricing plans modal.
 *
 * Clinician Max is the `MAX` `seat_type` of `CLINICIAN_PRO` — no own `SubscriptionTier`.
 * `/products` has no `seat_type`; match catalogue by name. Checkout uses `price_id` only.
 * Fold into `PricingPlanCard` once the tier splits.
 */
export function ClinicianMaxPricingPlanCard({
  isNonStripeManagedTeam,
  isNonAdminTeamMember = false,
  isTeamRoleUnresolved = false,
  isHighlighted,
}: {
  /** Team billing runs outside Stripe, so self-serve upgrades are unavailable. */
  isNonStripeManagedTeam: boolean;
  /** Team member who is not an admin, so self-serve upgrades are unavailable. */
  isNonAdminTeamMember?: boolean;
  /** Team role has not settled, so the upgrade stays closed without a role tooltip. */
  isTeamRoleUnresolved?: boolean;
  /** Tier-toned border, period toggle and CTA fill, when this is the card to steer towards. */
  isHighlighted?: boolean;
}) {
  const intl = useIntl();
  const setIsOpen = useSetAtom(pricingPlansModalOpenAtom);
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('annual');
  const selectedPeriod = billingPeriod === 'monthly' ? Period.ONE_MONTH : Period.ONE_YEAR;

  const { price, locale, copy, plan } = usePlanPriceDisplay({
    tier: SubscriptionTier.CLINICIAN_PRO,
    seatType: BillingSeatType.MAX,
    period: selectedPeriod,
  });
  const { clinicianMaxFeatures } = usePricingPlansModalFeatures();
  const { data: billingInfo } = useUserBillingInfo();
  const { isInReverseTrial, isFakeClinicianTrial, isEvidenceInFreeTrial } =
    useReverseTrial();
  const isTrialing = isInReverseTrial || isFakeClinicianTrial || isEvidenceInFreeTrial;
  const isCurrentPlan = isClinicianMaxBillingSeat(billingInfo);
  const currentPlan = billingInfo?.new_product_tier ?? billingInfo?.tier;
  const currentSeatType = billingInfo?.seat_type;
  const handleClose = useCallback(() => setIsOpen(false), [setIsOpen]);
  const { handleUpgradeByPriceId, isPending, isUpgradeInFlight } = useSubscriptionUpgrade(
    {
      onCheckoutComplete: handleClose,
    }
  );
  const ports = getCommerceBillingUiPorts();
  const complimentaryOffer = useComplimentaryYearlyMaxOffer({
    isHeidi2OptInEnabled: ports.useIsHeidi2OptInEnabled(),
    isTrialEndEnabled: ports.useIsHeidi2TrialEndEnabled(),
    isTrialing,
  });
  const trialOffer = useClinicianTrialYearlyMaxOffer({
    isTrialEndEnabled: ports.useIsHeidi2ClinicianTrialEndEnabled(),
    isAppleCompliantBuild: ports.isAppleCompliantBuild(),
    isOfferEnabled: ports.useIsHeidi2ClinicianTrialOfferEnabled(),
  });
  const yearlyMaxOffer = complimentaryOffer ?? trialOffer;

  const priceId = plan?.id;
  const isSelfServeUpgradeBlocked =
    !isCurrentPlan &&
    (isNonStripeManagedTeam || isNonAdminTeamMember || isTeamRoleUnresolved);

  const handleCtaClick = useCallback(() => {
    trackPricingPlansModalCta({
      action: isCurrentPlan
        ? ButtonAction.PricingPlansModalKeepCurrentPlan
        : ButtonAction.PricingPlansModalGetPlan,
      label: intl.formatMessage(isCurrentPlan ? messages.keepCta : messages.cta),
      currentPlan,
      currentSeatType,
      targetTier: SubscriptionTier.CLINICIAN_PRO,
      targetSeatType: BillingSeatType.MAX,
      period: selectedPeriod,
      isInReverseTrial,
      isFakeClinicianTrial,
    });

    // Dismiss: same MAX `price_id` would open a redundant Stripe session.
    if (isCurrentPlan) {
      handleClose();
      return;
    }

    if (!priceId || isUpgradeInFlight()) return;
    const applyDiscount =
      selectedPeriod === Period.ONE_YEAR
        ? yearlyMaxOffer?.applyDiscountForYearlyMax
        : undefined;
    void handleUpgradeByPriceId(selectedPeriod, priceId, {
      ...(applyDiscount != null && { applyDiscount }),
    });
  }, [
    handleClose,
    handleUpgradeByPriceId,
    intl,
    currentPlan,
    currentSeatType,
    isFakeClinicianTrial,
    isCurrentPlan,
    isInReverseTrial,
    isUpgradeInFlight,
    priceId,
    selectedPeriod,
    yearlyMaxOffer?.applyDiscountForYearlyMax,
  ]);

  return (
    <PricingPlanCardBase
      className={PRICING_PLANS_MODAL_CARD_CLASSES}
      isHighlighted={isHighlighted}
      planTitle={<FormattedMessage {...pricingPlansModalMessages.planNameClinicianMax} />}
      planSubtitle={
        <FormattedMessage {...pricingPlansModalMessages.planSubtitleClinicianMax} />
      }
      periodToggle={
        <PlanPeriodTabs
          value={billingPeriod}
          onValueChange={setBillingPeriod}
          saveBadgePlacement="start"
          size="sm"
          variant={isHighlighted ? 'proTier' : 'default'}
        />
      }
      priceSlot={<PlanPriceDisplay price={price} locale={locale} copy={copy} />}
      cta={
        getCommerceBillingUiPorts().isAppleCompliantBuild() ? null : (
          <PricingPlanCardCtaButton
            variant={isHighlighted ? 'pro' : 'outline'}
            icon={<TierProFilled size={16} />}
            ctaText={
              <FormattedMessage {...(isCurrentPlan ? messages.keepCta : messages.cta)} />
            }
            isCtaDisabled={
              isPending || isSelfServeUpgradeBlocked || (!isCurrentPlan && !priceId)
            }
            onClick={handleCtaClick}
            data-testid="clinician-max-pricing-plan-cta"
          />
        )
      }
      isCtaDisabled={isSelfServeUpgradeBlocked}
      ctaDisabledTooltip={
        !isSelfServeUpgradeBlocked || isTeamRoleUnresolved
          ? undefined
          : isNonAdminTeamMember
            ? intl.formatMessage(pricingPlansModalMessages.contactTeamAdminToUpgrade)
            : intl.formatMessage(pricingPlansModalMessages.contactSalesToUpgrade)
      }
      featuresSectionTitle={
        <FormattedMessage {...pricingPlansModalMessages.featuresEverythingInClinician} />
      }
      features={clinicianMaxFeatures}
    />
  );
}
